import {
  ApartmentOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  UserOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { type AIPlatform } from "@p0security/shared/types/ai-inventory";
import { Button, Card, Flex } from "antd";
import { useCallback, useMemo } from "react";
import { color, font, radius } from "theme/aiInventory";

import { CompositionBar } from "./CompositionBar.tsx";
import { TrendSparkline } from "./TrendSparkline.tsx";

export const ALL_PLATFORMS = "all" as const;
export type PlatformSelection = AIPlatform | typeof ALL_PLATFORMS;

export type HeaderCountCounts = {
  agentsConnectors: number;
  owner: number;
  risks: number;
  status: number;
};

export type HeaderCountTrends = {
  agentsConnectors: number[];
  owner: number[];
  risks: number[];
  status: number[];
};

export type BreakdownItem = {
  key: string;
  label: string;
  value: number;
  tone?: string;
};

export type HeaderCardsBreakdowns = {
  status: BreakdownItem[];
  owner: BreakdownItem[];
  agentsConnectors: BreakdownItem[];
  risks: BreakdownItem[];
};

type FilterController = {
  activeKey?: string;
  onSelect: (key: string | undefined) => void;
};

export type HeaderCardsProps = {
  counts: HeaderCountCounts;
  trends: HeaderCountTrends;
  breakdowns: HeaderCardsBreakdowns;
  statusFilter: FilterController;
  riskFilter: FilterController;
};

const CARD_STYLE: React.CSSProperties = {
  flex: 1,
  minWidth: 160,
  background: color.card,
  border: `1px solid ${color.rule}`,
  borderRadius: radius.sharp,
};

const kickerStyle: React.CSSProperties = {
  fontFamily: font.mono,
  fontSize: 10,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: color.inkMute,
};

type StatCardProps = {
  title: string;
  value: number;
  trend: number[];
  accent: string;
  icon: React.ReactNode;
  breakdown?: BreakdownItem[];
  filter?: FilterController;
};

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  trend,
  accent,
  icon,
  breakdown,
  filter,
}) => {
  const activeKey = filter?.activeKey;
  const onSelect = filter?.onSelect;
  const activeItem = useMemo(
    () => breakdown?.find((b) => b.key === activeKey),
    [breakdown, activeKey]
  );
  const handleClearFilter = useCallback(
    () => onSelect?.(undefined),
    [onSelect]
  );
  const segments = useMemo(
    () =>
      breakdown?.map((b) => ({
        key: b.key,
        color: b.tone ?? color.inkMute,
        label: b.label,
        value: b.value,
      })) ?? [],
    [breakdown]
  );
  const headlineValue = activeItem ? activeItem.value : value;
  const headlineLabel = activeItem ? activeItem.label : "Total";
  return (
    <Card
      size="small"
      style={{
        ...CARD_STYLE,
        border: `1px solid ${activeKey ? color.ink : color.rule}`,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          ...kickerStyle,
        }}
      >
        <span
          aria-hidden
          style={{ color: color.inkMute, fontSize: 12, display: "inline-flex" }}
        >
          {icon}
        </span>
        <span>{title}</span>
      </div>
      <Flex gap={20} align="stretch">
        <Flex justify="flex-start">
          <span style={{ whiteSpace: "nowrap" }}>
            {headlineValue} <strong>{headlineLabel}</strong>
            {activeItem && onSelect && (
              <Button
                type="text"
                size="small"
                aria-label="Clear filter"
                onClick={handleClearFilter}
                icon={<CloseOutlined style={{ fontSize: 11 }} />}
                style={{ marginLeft: 6, color: color.inkMute }}
              />
            )}
          </span>
        </Flex>
        {trend.length > 1 ? (
          <TrendSparkline data={trend} color={accent} height={40} />
        ) : (
          <span
            style={{
              fontSize: 10,
              color: color.inkMute,
              fontFamily: font.mono,
            }}
          >
            {trend.length === 0 ? "no history yet" : "awaiting next run"}
          </span>
        )}
      </Flex>
      {segments.length > 0 && (
        <div style={{ marginTop: 8 }}>
          <CompositionBar
            segments={segments}
            activeKey={activeKey}
            onSelect={filter?.onSelect}
            height={6}
          />
        </div>
      )}
    </Card>
  );
};

export const HeaderCards: React.FC<HeaderCardsProps> = ({
  counts,
  trends,
  breakdowns,
  statusFilter,
  riskFilter,
}) => {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "stretch",
      }}
    >
      <StatCard
        title="Statuses"
        value={counts.status}
        trend={trends.status}
        accent={color.integration}
        icon={<CheckCircleOutlined />}
        breakdown={breakdowns.status}
        filter={statusFilter}
      />
      <StatCard
        title="Owners"
        value={counts.owner}
        trend={trends.owner}
        accent={color.owner}
        icon={<UserOutlined />}
        breakdown={breakdowns.owner}
      />
      <StatCard
        title="Agents and connectors"
        value={counts.agentsConnectors}
        trend={trends.agentsConnectors}
        accent={color.agentsConnectors}
        icon={<ApartmentOutlined />}
        breakdown={breakdowns.agentsConnectors}
      />
      <StatCard
        title="Risks"
        value={counts.risks}
        trend={trends.risks}
        accent={color.riskUrgent}
        icon={<WarningOutlined />}
        breakdown={breakdowns.risks}
        filter={riskFilter}
      />
    </div>
  );
};
